'use client';

import {
  useBlogSource,
  parseMarkdown,
  ArticleTopBlocks,
  ArticleBottomBlocks,
  ArticleSidebarBlocks,
  useArticleBlockGroups,
  type Article,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { BlockRoot } from '../_shared/BlockRoot';
import { flatCoverBg, type BlogHeroVariant } from '../blog/BlogHeroVariants';
import { resolveArticleCard, type ArticleCardVariant } from '../cards/ArticleCard';
import { sectionPad, sectionInner } from '../_shared/styles';

// Stand-in passed to useArticleBlockGroups while the article fetch is
// still in flight — module-level so its identity (and therefore the
// hook's memo) stays stable across renders.
const EMPTY_ARTICLE: { blocks?: Article['blocks'] } = {};

/**
 * Long-form article reader. Fetches by slug, renders an article header
 * ({@link ArticleHeader}) + markdown body + sidebar/inline/bottom block
 * zones + related-articles grid. Self-pads via {@code sectionPad} /
 * {@code sectionInner}.
 *
 * <p>Per-article block overrides come through {@code article.blocks}
 * and are merged with the host's {@code articleTemplate.blocks} via
 * the runtime's article-block pipeline.
 *
 * <p>The related-articles grid below is the ORIGINAL, non-configurable
 * one: same collection, same category, three cards, a hardcoded
 * heading. Its placeable successor is the host's {@code
 * RelatedArticles} block (source picker, relation strategy, authored
 * labels), which the current backend's {@code EntityBody} detail pages
 * use. A page that renders both should turn this one off via
 * {@code config.showRelated}.
 */
export interface BlogDetailLayoutProps {
  slug: string;
  /** Per-tenant blog module config — drives heroVariant /
   *  showAuthor / showRelated / articleTemplate / etc. */
  config?: {
    /** Landing-only: picks the featured-story hero on the blog INDEX.
     *  The detail page has its own {@link ArticleHeader} — a teaser hero
     *  here would link to the page it is already on. */
    blogHeroVariant?: BlogHeroVariant;
    articleCardVariant?: ArticleCardVariant;
    showAuthor?: boolean;
    showReadTime?: boolean;
    showRelated?: boolean;
    articleTemplate?: { blocks?: unknown[] };
  };
  relatedLimit?: number;
  articleHrefTemplate?: string;
  themeId?: string | null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Header of the article being read.
 *
 * <p>NOT one of the {@code BlogHero*} variants: those are LANDING
 * teasers for a featured story — they wrap everything in a link to the
 * article, print a literal "Featured" eyebrow, title the story as an
 * {@code <h2>}, and paint a 21:9 brand gradient when the story has no
 * cover. On a detail page that reads as a self-link to the page you are
 * already on, plus a large empty coloured box above the body.
 *
 * <p>Here: cover only when there IS one, {@code <h1>} title (the page's
 * real heading), and a plain meta line. Nothing renders for a field the
 * article doesn't have.
 */
function ArticleHeader({
  title,
  excerpt,
  date,
  author,
  authorRole,
  category,
  readTime,
  cover,
}: {
  title: string;
  excerpt?: string;
  date?: string;
  author?: string;
  authorRole?: string;
  category?: string;
  readTime?: string;
  cover?: string;
}) {
  const meta = [author && authorRole ? `${author} · ${authorRole}` : author, date, readTime].filter(
    Boolean,
  );

  return (
    <header style={{ marginBottom: 32 }}>
      {cover ? (
        <div
          aria-hidden
          style={{
            aspectRatio: '21 / 9',
            background: flatCoverBg(cover),
            borderRadius: 'var(--radius-md, 8px)',
            marginBottom: 20,
          }}
        />
      ) : null}
      {/* {category ? (
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: 'var(--font-tracking-widest, 0.14em)',
            textTransform: 'uppercase',
            color: 'var(--color-brand-600, #4f46e5)',
          }}
        >
          {category}
        </span>
      ) : null} */}
      <h1
        style={{
          // margin: `${category ? 'var(--space-2, 8px)' : '0'} 0 0`,
          fontSize: 'clamp(var(--font-size-2xl, 28px), 3.2vw, var(--font-size-4xl, 48px))',
          fontWeight: 700,
          lineHeight: 1.15,
          letterSpacing: 'var(--font-tracking-snug, -0.015em)',
        }}
      >
        {title}
      </h1>
      {excerpt ? (
        <p
          style={{
            margin: '12px 0 0',
            fontSize: 'var(--font-size-lg, 18px)',
            lineHeight: 1.5,
            color: 'var(--color-text-secondary, #71717a)',
            maxWidth: 720,
          }}
        >
          {excerpt}
        </p>
      ) : null}
      {meta.length > 0 ? (
        <div
          style={{
            marginTop: 12,
            fontSize: 'var(--font-size-sm, 13px)',
            color: 'var(--color-text-secondary, #71717a)',
          }}
        >
          {meta.join(' · ')}
        </div>
      ) : null}
    </header>
  );
}

function prettify(s: string): string {
  return s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(' ');
}

export function BlogDetailLayout({
  slug,
  config,
  relatedLimit = 3,
  articleHrefTemplate = '/blog/{slug}',
  themeId,
}: BlogDetailLayoutProps) {
  const blog = useBlogSource();

  const state = useAsyncResource<{ article: Article; related: Article[] }>(async () => {
    if (!blog) return null;
    const article = await blog.getArticleBySlug(slug);
    if (!article) return null;
    let related: Article[] = [];
    if (config?.showRelated !== false && article.category) {
      const r = await blog.listArticles({
        filters: { category: { $eq: article.category } },
        sort: ['publishedAt:desc'],
        pagination: { page: 1, pageSize: relatedLimit + 1 },
      });
      related = r.items.filter((a) => a.slug !== article.slug).slice(0, relatedLimit);
    }
    return { article, related };
  }, [blog, slug, config?.showRelated, relatedLimit]);

  // Article-block override groupings. Called unconditionally (with a
  // blocks-less stand-in article before the fetch resolves) so the
  // hook order stays stable across the loading → loaded transition —
  // the early returns below must not skip over any hook call.
  const groups = useArticleBlockGroups(
    state.kind === 'loaded' ? state.data.article : EMPTY_ARTICLE,
    config,
    'blog',
  );

  if (!blog) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="BlogDetailLayout" adapterName="BlogSource" />
        </div>
      </BlockRoot>
    );
  }

  if (state.kind !== 'loaded') return null;
  const { article, related } = state.data;
  const ArticleCard = resolveArticleCard(config?.articleCardVariant ?? 'image-led');
  const showAuthor = config?.showAuthor !== false;
  const showReadTime = config?.showReadTime !== false;
  const showRelated = config?.showRelated !== false;

  // CMS-authored articles carry markup; only fall back to the
  // markdown/plain-text splitter when there is no HTML body.
  const html = article.bodyHtml?.trim();
  const paragraphs = html ? [] : parseMarkdown(article.body);

  return (
    <BlockRoot as="article" style={sectionPad}>
      <div style={sectionInner}>
        <ArticleHeader
          title={article.title}
          excerpt={article.excerpt}
          date={formatDate(article.publishedAt)}
          author={showAuthor ? article.author?.name : undefined}
          authorRole={showAuthor ? article.author?.role : undefined}
          category={article.category ? prettify(article.category) : undefined}
          readTime={showReadTime ? article.readTime : undefined}
          cover={article.cover}
        />

        <ArticleTopBlocks article={article} config={config} kind="blog" themeId={themeId ?? null} />

        <div
          className={
            groups.sidebar.length > 0 ? 'grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px]' : ''
          }
          style={{
            display: 'grid',
            gridTemplateColumns: groups.sidebar.length > 0 ? undefined : 'minmax(0, 1fr)',
            gap: 32,
            marginTop: 32,
          }}
        >
          <div
            className={html ? 'lms-rich-text' : undefined}
            style={{
              fontSize: 'var(--font-size-md, 16px)',
              lineHeight: 1.75,
              color: 'var(--color-text-primary, #18181b)',
              fontFamily: 'var(--font-family-body, Georgia, serif)',
            }}
          >
            {html && <div dangerouslySetInnerHTML={{ __html: html }} />}
            {paragraphs.map((b, i) => {
              if (b.type === 'h1')
                return (
                  <h1
                    key={i}
                    style={{
                      fontSize:
                        'clamp(var(--font-size-2xl, 28px), 3vw, var(--font-size-3xl, 36px))',
                      fontWeight: 700,
                      marginTop: 32,
                    }}
                  >
                    {b.text}
                  </h1>
                );
              if (b.type === 'h2')
                return (
                  <h2
                    key={i}
                    style={{
                      fontSize: 'var(--font-size-2xl, 28px)',
                      fontWeight: 600,
                      marginTop: 28,
                    }}
                  >
                    {b.text}
                  </h2>
                );
              return (
                <p key={i} style={{ marginTop: 16 }}>
                  {b.text}
                </p>
              );
            })}
          </div>

          {groups.sidebar.length > 0 && (
            <ArticleSidebarBlocks
              article={article}
              config={config}
              kind="blog"
              themeId={themeId ?? null}
            />
          )}
        </div>

        <ArticleBottomBlocks
          article={article}
          config={config}
          kind="blog"
          themeId={themeId ?? null}
        />

        {/* {showRelated && related.length > 0 && (
          <section style={{ marginTop: 64 }}>
            <h2
              style={{ fontSize: 'var(--font-size-xl, 22px)', fontWeight: 600, marginBottom: 16 }}
            >
              Related articles
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 20,
              }}
            >
              {related.map((a) => (
                <ArticleCard
                  key={a.id}
                  href={articleHrefTemplate.replace('{slug}', encodeURIComponent(a.slug))}
                  title={a.title}
                  excerpt={a.excerpt}
                  date={formatDate(a.publishedAt)}
                  author={showAuthor ? a.author?.name : undefined}
                  category={a.category ? prettify(a.category) : undefined}
                  readTime={showReadTime ? a.readTime : undefined}
                  cover={a.cover}
                />
              ))}
            </div>
          </section>
        )} */}
      </div>
    </BlockRoot>
  );
}
